import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const CMSCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    parent_id: '',
    description: '',
    is_enabled: true,
    display_order: 0,
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const res = await api.getEcommerceCategories();
      if (res.success) setCategories(res.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const slugify = (text) =>
    String(text)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const handleOpenCreate = () => {
    setEditingCategory(null);
    setForm({
      name: '',
      slug: '',
      parent_id: '',
      description: '',
      is_enabled: true,
      display_order: 0,
    });
    setShowModal(true);
  };

  const handleOpenEdit = (cat) => {
    setEditingCategory(cat);
    setForm({
      name: cat.name,
      slug: cat.slug || slugify(cat.name),
      parent_id: cat.parent_id || '',
      description: cat.description || '',
      is_enabled: Boolean(cat.is_enabled),
      display_order: cat.display_order ?? 0,
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : name === 'display_order' ? parseInt(value, 10) || 0 : value,
    }));
    if (name === 'name' && !editingCategory) {
      setForm((prev) => ({ ...prev, slug: slugify(value) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingCategory) {
        await api.updateEcommerceCategory(editingCategory.id, form);
      } else {
        await api.createEcommerceCategory(form);
      }
      setShowModal(false);
      loadCategories();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? Child categories may need to be moved or deleted first.')) return;
    try {
      await api.deleteEcommerceCategory(id);
      loadCategories();
    } catch (e) {
      setError(e.message);
    }
  };

  const ROOT = 0;
  const byParent = {};
  categories.forEach((c) => {
    const pid = c.parent_id == null || c.parent_id === '' ? ROOT : Number(c.parent_id);
    if (!byParent[pid]) byParent[pid] = [];
    byParent[pid].push(c);
  });
  const renderRows = (parentId) => {
    const list = byParent[parentId] || [];
    return list.map((c) => (
      <React.Fragment key={c.id}>
        <tr>
          <td>{c.parent_name ? `↳ ${c.name}` : c.name}</td>
          <td><code>{c.slug}</code></td>
          <td>
            <span className={`badge ${c.is_enabled ? 'bg-success' : 'bg-secondary'}`}>
              {c.is_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </td>
          <td>{c.display_order}</td>
          <td>
            <button type="button" className="btn btn-sm btn-info mr-1" onClick={() => handleOpenEdit(c)}>
              <i className="fas fa-edit"></i>
            </button>
            <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(c.id)}>
              <i className="fas fa-trash"></i>
            </button>
          </td>
        </tr>
        {renderRows(c.id)}
      </React.Fragment>
    ));
  };

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-12">
          <button type="button" className="btn btn-primary btn-sm" onClick={handleOpenCreate}>
            <i className="fas fa-plus"></i> New Category
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Categories</h3>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center p-4">Loading...</div>
          ) : (
            <table className="table table-bordered table-hover mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Order</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">No categories yet</td>
                  </tr>
                ) : (
                  renderRows(ROOT)
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-backdrop fade show" onClick={() => setShowModal(false)} style={{ zIndex: 1040 }} />
          <div className="modal-dialog" style={{ zIndex: 1050, position: 'relative' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">{editingCategory ? 'Edit Category' : 'New Category'}</h4>
                <button type="button" className="close" onClick={() => setShowModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Slug *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="slug"
                      value={form.slug}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Parent category</label>
                    <select
                      className="form-control"
                      name="parent_id"
                      value={form.parent_id}
                      onChange={handleChange}
                    >
                      <option value="">— None (root) —</option>
                      {categories.filter((c) => String(c.id) !== String(editingCategory?.id)).map((c) => (
                        <option key={c.id} value={c.id}>{c.parent_name ? `↳ ` : ''}{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      rows="2"
                      value={form.description}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Display order</label>
                    <input
                      type="number"
                      className="form-control"
                      name="display_order"
                      value={form.display_order}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <div className="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="cat_enabled"
                        name="is_enabled"
                        checked={form.is_enabled}
                        onChange={handleChange}
                      />
                      <label className="custom-control-label" htmlFor="cat_enabled">Enabled</label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-default btn-sm" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm">
                    {editingCategory ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CMSCategories;
